<script>
import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import PanelEmptyState from './sidebar/PanelEmptyState.vue';
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import { CONVERSATION_STATUS } from 'shared/constants/messages';

export default {
  components: {
    ConversationCard,
    PanelEmptyState,
    Spinner,
  },
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
    conversationId: {
      type: [String, Number],
      required: true,
    },
  },
  computed: {
    conversations() {
      return this.$store.getters['contactConversations/getContactConversation'](
        this.contactId
      );
    },
    previousConversations() {
      const filtered = this.conversations.filter(
        conversation => conversation.id !== Number(this.conversationId)
      );

      // Ordenar conversas: abertas primeiro, depois por mensagem mais recente
      return filtered.sort((a, b) => {
        // Primeiro, priorizar conversas abertas
        const statusPriorityA = a.status === CONVERSATION_STATUS.OPEN ? 0 : 1;
        const statusPriorityB = b.status === CONVERSATION_STATUS.OPEN ? 0 : 1;

        if (statusPriorityA !== statusPriorityB) {
          return statusPriorityA - statusPriorityB;
        }

        // Se ambas têm o mesmo status, ordenar por última atividade (mais recente primeiro)
        const timestampA =
          a.last_activity_at || a.timestamp || a.created_at || 0;
        const timestampB =
          b.last_activity_at || b.timestamp || b.created_at || 0;

        return new Date(timestampB) - new Date(timestampA);
      });
    },
    ...mapGetters({
      uiFlags: 'contactConversations/getUIFlags',
    }),
  },
  watch: {
    contactId(newContactId, prevContactId) {
      if (newContactId && newContactId !== prevContactId) {
        this.$store.dispatch('contactConversations/get', newContactId);
      }
    },
  },
  mounted() {
    this.$store.dispatch('contactConversations/get', this.contactId);
  },
};
</script>

<template>
  <div class="contact-conversation--panel">
    <div v-if="!uiFlags.isFetching" class="contact-conversation__wrap">
      <PanelEmptyState
        v-if="!previousConversations.length"
        :message="$t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND')"
      />
      <div v-else class="contact-conversation--list">
        <!-- As duas classes fazem coisas diferentes, e por isso convivem:
             `compact` e a variante do proprio `ConversationCard` para este
             painel (sem avatar, largura cheia) e continua valendo para a
             historia do histoire; `panel-conversation` e o gancho do desenho da
             #1243 -- cartao com contorno e fundo rebaixado, como os de fluxo --
             e existe como classe propria para as regras abaixo ganharem do
             `&.compact` do componente sem depender da ordem da folha. -->
        <ConversationCard
          v-for="conversation in previousConversations"
          :key="conversation.id"
          :chat="conversation"
          :hide-inbox-name="false"
          hide-thumbnail
          class="compact panel-conversation"
        />
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.no-label-message {
  @apply text-subtle mb-4;
}

// Cartoes empilhados com a mesma folga da lista de fluxos: na referencia da
// #1243 as duas secoes tem o mesmo ritmo.
.contact-conversation--list {
  @apply flex flex-col gap-1.5;
}

// A forma vem dos cartoes de fluxo -- mesmo fundo rebaixado, mesmo contorno,
// mesmo raio. Na lista de conversas o cartao e uma LINHA de largura cheia
// separada por divisoria; aqui ele e um item de uma coluna estreita, e linha
// full-bleed dentro de uma secao com titulo nao le como item.
//
// O seletor comeca em `.contact-conversation--panel` de proposito: e ele que
// recebe o atributo de escopo, o que leva a regra a (0,4,0) e a poe acima do
// `.conversation.compact[data-v]` do proprio `ConversationCard` -- que vale
// (0,3,0) e empataria.
.contact-conversation--panel ::v-deep .conversation.panel-conversation {
  @apply px-2.5 py-2 border rounded-lg bg-surface-sunken dark:bg-surface-raised
    border-line-faint transition-colors duration-150;

  &:hover {
    @apply border-line bg-surface-sunken dark:bg-surface-raised;
  }

  // Um degrau abaixo da lista de conversas em cada papel: o cartao aqui nao
  // disputa a atencao com a conversa aberta, ele e referencia de apoio.
  .conversation--user {
    @apply text-xs font-medium;
  }

  .conversation--message {
    @apply text-xs leading-4;
  }

  .meta-chip,
  .conversation--time {
    @apply text-xxs;
  }
}

// O bloco `::v-deep .conversation { pr-0; .conversation--details { pl-2 } }`
// saiu daqui. Ele existia para o layout de card, onde a folga direita vinha do
// `mx-2` do proprio card; com a linha full-bleed o `pr-0` vencia o `px-5` por
// especificidade (0,2,0 contra 0,1,0) e colava o status e o horario na borda do
// acordeao. `.conversation--details` era regra orfa: a classe nao existe mais.
// Quem governa o padding deste contexto agora e o `&.compact` do proprio
// ConversationCard.
</style>
